'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AuthContext,
  BlockAttrConfigProvider,
  CartProvider,
  CommerceSourceProvider,
  SearchSourceProvider,
} from '@akademi/site-builder-runtime';
import { BlocksI18nProvider } from './BlocksI18nProvider';
import { createStorefrontCommerceSource } from '@/src/lib/commerce/createStorefrontCommerceSource';
import { createStorefrontSearchSource } from '@/src/lib/commerce/createStorefrontSearchSource';
import { createHospitalitySource } from '@/src/lib/hospitality/createHospitalitySource';
import { HospitalitySourceProvider } from '@/src/lib/hospitality/hospitalitySource';
import { PriceMessagesProvider } from '@/src/components/catalogue/VatDisplay';
import { HeaderChromeProvider } from '@/src/extensions/headerChromeContext';
import type { StorefrontExtension } from '@/src/extensions/types';

/**
 * Mounts the host data sources the blocks read through — once, near the root.
 *
 * <p>`CommerceSource` is the whole reason this app exists as a host: a product
 * grid, a facet panel or a kit configurator is portable precisely because it
 * never calls `fetch` itself, and this is where "where do the bytes come from"
 * is answered for the storefront.
 *
 * <p>`HospitalitySource` is the same contract for the restaurant vocabulary —
 * site, menu, availability, bookings, bills. It is mounted unconditionally
 * rather than behind a "is this a restaurant tenant?" flag: the host does not
 * know what an operator has put on a page, and a source whose `site()` answers
 * 404 on a non-hospitality channel is exactly the empty state the blocks
 * already render. A conditional here would mean a block silently disappearing
 * on a tenant that had just been given one.
 *
 * <p>`CartProvider` holds the shopper's basket for every block on the page, so
 * an Add on a product page and the lines on the basket page are one basket. It
 * creates nothing until the first Add: a visitor who only browses costs no row.
 *
 * <p>`SearchSource` backs the header search box. Without it the box renders but
 * its dropdown never fires a request, which is the state this app was in: the
 * shared header defaults to the learning catalog's search endpoint, and the
 * storefront does not serve one, so every keystroke 404'd.
 *
 * <p>`BlocksI18nProvider` is the catalogue the blocks' own strings come from.
 * Without it every `t()` call in every block returned its key, so the pages
 * read `commerce.cart.continue` instead of "Continue shopping" — see that file.
 *
 * <p>`AuthContext` remains the legacy framework gate. Buying lists use the
 * storefront's actual account profile and never infer sign-in from a route.
 */
export function StorefrontAdapterProviders({
  locale,
  priceMessages,
  headerChrome,
  children,
}: {
  locale: string;
  /** The app's `price.*` strings, for the header's inc/ex VAT switch. */
  priceMessages?: Record<string, string>;
  /** This tenant's vertical header chrome, resolved on the server from `enabledPlugins`. */
  headerChrome?: StorefrontExtension['headerChrome'];
  children: ReactNode;
}) {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? '';
  const [accountScope, setAccountScope] = useState<string>();
  const authEpoch = useRef(0);
  const accountProbe = useMemo(() => createStorefrontCommerceSource({ basePath, locale }), [basePath, locale]);
  const activeLists = useRef<ReturnType<typeof createStorefrontCommerceSource>['buyingLists']>(undefined);

  useEffect(() => {
    let generation = 0;
    const probe = () => {
      const current = ++generation;
      const nonce = ++authEpoch.current;
      activeLists.current?.invalidate();
      setAccountScope(undefined);
      void Promise.allSettled([accountProbe.accountProfile?.() ?? Promise.resolve(null), accountProbe.myTradeAccount?.() ?? Promise.resolve(null)]).then(([profileResult, tradeResult]) => {
        if (current !== generation) return;
        const profile = profileResult.status === 'fulfilled' ? profileResult.value : null;
        const trade = tradeResult.status === 'fulfilled' ? tradeResult.value : null;
        const email = profile?.email?.trim().toLowerCase();
        const host = globalThis.location?.host;
        setAccountScope(email && host ? JSON.stringify([host, email, trade?.accountNumber ?? null, nonce]) : undefined);
      }).catch(() => { if (current === generation) setAccountScope(undefined); });
    };
    probe();
    window.addEventListener('storefront-account-changed', probe);
    window.addEventListener('focus', probe);
    return () => { generation++; activeLists.current?.invalidate(); window.removeEventListener('storefront-account-changed', probe); window.removeEventListener('focus', probe); };
  }, [accountProbe]);

  const commerceSource = useMemo(
    () => createStorefrontCommerceSource({ basePath, locale, buyingListsScopeKey: accountScope }),
    [basePath, locale, accountScope],
  );
  activeLists.current = commerceSource.buyingLists;

  const searchSource = useMemo(() => createStorefrontSearchSource({ basePath }), [basePath]);

  const hospitalitySource = useMemo(
    () => createHospitalitySource({ basePath, locale }),
    [basePath, locale],
  );

  return (
    <BlockAttrConfigProvider namespace={process.env.NEXT_PUBLIC_BLOCK_ATTR_NS || undefined}>
      <BlocksI18nProvider locale={locale}>
        <AuthContext.Provider value={null}>
          <CommerceSourceProvider value={commerceSource}>
            <HospitalitySourceProvider value={hospitalitySource}>
              <SearchSourceProvider value={searchSource}>
                <CartProvider>
                  <PriceMessagesProvider messages={priceMessages}>
                    <HeaderChromeProvider value={headerChrome}>{children}</HeaderChromeProvider>
                  </PriceMessagesProvider>
                </CartProvider>
              </SearchSourceProvider>
            </HospitalitySourceProvider>
          </CommerceSourceProvider>
        </AuthContext.Provider>
      </BlocksI18nProvider>
    </BlockAttrConfigProvider>
  );
}
